import { Box } from "@mui/material";
import { Header } from "@tanstack/react-table";
import { useTranslation } from "next-i18next";
import { ITableScrollableCellProps, TableScrollableCell } from "src/components-mui/base/table-scrollable/components/cell/table-scrollable-cell";
import { TableSortingIcon } from "src/components-mui/base/table-scrollable/components/table-sorting-icon";
import { TABLE_CELL_MIN_WIDTH } from "src/components-mui/base/table-scrollable/constants";
import { WfTooltip } from "src/components-mui/base/wf-tooltip/wf-tooltip";
import { getAriaSort } from "src/utils/table/get-aria-sort";

interface ITableScrollableHeaderCellProps<T> extends ITableScrollableCellProps {
    header?: Header<T, unknown>;
    tooltip?: string;
}

export const TableScrollableHeaderCell = <T,>({ header, children, shrinkable, tooltip, sx, ...props }: ITableScrollableHeaderCellProps<T>) => {
    const { t } = useTranslation("table-scrollable");
    const isSortingEnabled = header && header.column.columnDef.enableSorting !== false;
    const sortDirection = header?.column.getIsSorted();
    const ariaSort = isSortingEnabled ? getAriaSort(sortDirection ?? false) : undefined;

    // onClick on TableCell was added because child of Td cannot stretch to parent height. onClick on Box is kept for accessibility (focus, keyboard navigation)
    return (
        <TableScrollableCell
            shrinkable={shrinkable}
            contentSx={{ display: "flex" }}
            sx={{
                paddingY: 2,
                verticalAlign: "middle",
                cursor: isSortingEnabled ? "pointer" : "initial",
                "&:hover button": isSortingEnabled ? { color: "gray.600" } : {},
                minWidth: TABLE_CELL_MIN_WIDTH,
                ...sx,
            }}
            onClick={isSortingEnabled ? header.column.getToggleSortingHandler() : undefined}
            aria-sort={ariaSort}
            {...props}
        >
            <Box
                component={isSortingEnabled ? "button" : "div"}
                type={isSortingEnabled ? "button" : undefined}
                onClick={isSortingEnabled ? header.column.getToggleSortingHandler() : undefined}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    border: "none",
                    background: "none",
                    padding: 0,
                    textAlign: "inherit",
                    justifyContent: shrinkable ? "flex-start" : "flex-end",
                }}
            >
                <Box sx={{ mr: 0.5, whiteSpace: "pre", lineHeight: 1.5 }}>{children}</Box>
                {tooltip && (
                    <Box sx={{ display: { xs: "none", sm: "none", md: "flex" }, mr: 0.5 }}>
                        <WfTooltip content={tooltip} iconAs="span" ariaLabel={t("buttons.show-additional-information")} />
                    </Box>
                )}
                {header && <TableSortingIcon header={header} />}
            </Box>
        </TableScrollableCell>
    );
};
